import { useEffect } from "react";
import { useLocation, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Globe2, CheckCircle2, MessageCircle } from "lucide-react";
import { LOCALE_CONFIGS } from "@/data/localeConfigs";
import { SERVICE_CONFIGS } from "@/data/serviceConfigs";
import FaqSection from "@/components/common/FaqSection";
import RelatedCaseStudies from "@/components/common/RelatedCaseStudies";
import Industries from "@/components/home/Industries";
import Seo, { breadcrumbLd } from "@/components/seo/Seo";
import { trackEvent, trackViewOnce } from "@/lib/analytics";

const openEstimateWizard = (source) => {
  // NB: don't push project_estimate_started here — the global
  // <ProjectEstimateWizard/> listener fires it exactly once when it opens.
  // Pushing twice would create duplicate GA4 events.
  window.dispatchEvent(new CustomEvent("open-estimate-wizard", { detail: { source } }));
};

const trackConsultation = (source) => {
  trackEvent("book_consultation_clicked", { source });
};

/**
 * LocalePage — market-tuned landing page mounted at
 *   /uk /us /pk /de /sa /ae /europe.
 *
 * Sections: Hero → Business problems → Core services → Related case studies
 *   → Why CloudLab → Reused Industries block → FAQ → Dual-CTA footer.
 * Every section is optional and renders only if the market config populates it.
 */
export default function LocalePage() {
  const { pathname } = useLocation();
  const locale = pathname.replace(/^\//, "").split("/")[0];
  const c = LOCALE_CONFIGS[locale];

  useEffect(() => {
    if (!c) return;
    trackViewOnce("market_page_view", { market: c.slug });
  }, [c]);

  if (!c) return <Navigate to="/" replace />;

  return (
    <div data-testid={`locale-page-${c.slug}`}>
      <Seo
        title={c.seoTitle || `${c.countryName} · CloudLab Technologies`}
        description={c.description}
        keywords={c.keywords}
        canonical={`https://www.cloudlabweb.com/${c.slug}`}
        jsonLd={breadcrumbLd([
          { name: "Home", path: "/" },
          { name: c.countryName, path: `/${c.slug}` },
        ])}
      />

      {/* ---- HERO ---- */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(1100px 500px at 15% 0%, hsl(var(--primary) / 0.12) 0%, transparent 60%), radial-gradient(900px 400px at 100% 20%, hsl(var(--secondary) / 0.10) 0%, transparent 60%)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 pt-16 md:pt-24 pb-10 md:pb-16">
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 border border-border/60 bg-background/70 backdrop-blur-sm">
            <span className="text-base leading-none">{c.flag}</span>
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span className="text-[11px] font-mono uppercase tracking-[0.22em]">{c.hero.kicker}</span>
          </div>
          <h1
            className="mt-6 font-heading font-black tracking-tight leading-[0.98] text-5xl md:text-6xl lg:text-[5.25rem] max-w-5xl"
            data-testid={`${c.slug}-hero-title`}
          >
            {c.hero.title}{" "}
            <span className="italic underline decoration-[3px] underline-offset-[6px] text-primary decoration-primary">
              {c.hero.highlight}
            </span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed">
            {c.hero.subtitle}
          </p>
          {c.localCopy && (
            <p className="mt-4 text-base text-muted-foreground max-w-2xl leading-relaxed">{c.localCopy}</p>
          )}

          {/* Dual CTAs — Estimate + Consultation */}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              onClick={() => openEstimateWizard(`locale_${c.slug}_hero`)}
              className="group rounded-full pl-6 pr-2 py-2 inline-flex items-center gap-3 text-sm font-medium bg-primary text-primary-foreground shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.6)]"
              data-testid={`${c.slug}-cta-primary`}
            >
              <span>Get a Free Project Estimate</span>
              <span className="w-8 h-8 rounded-full bg-background text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                <ArrowRight className="w-4 h-4" />
              </span>
            </button>
            <Link
              to="/contact"
              onClick={() => trackConsultation(`locale_${c.slug}_hero`)}
              className="rounded-full px-6 py-3 border border-border text-sm font-medium hover:bg-foreground/5 transition-colors"
              data-testid={`${c.slug}-cta-secondary`}
            >
              Book a 15-Minute Consultation
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl">
            {c.hero.stats.map((s, i) => (
              <motion.div
                key={s.l}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + i * 0.05, duration: 0.4 }}
              >
                <div className="font-heading font-black text-3xl md:text-4xl leading-none text-primary">{s.v}</div>
                <div className="mt-2 text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground leading-snug">
                  {s.l}
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 inline-flex items-center gap-2 text-xs text-muted-foreground">
            <Globe2 className="w-3.5 h-3.5" /> {c.officesHint}
          </div>
        </div>
      </section>

      {/* ---- BUSINESS PROBLEMS ---- */}
      {c.problems?.length > 0 && (
        <section className="py-14 md:py-20 bg-muted/30" data-testid={`${c.slug}-problems`}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="max-w-2xl">
              <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">Business problems we solve</div>
              <h2 className="mt-2 font-heading font-bold text-3xl md:text-4xl tracking-tight leading-[1.05]">
                Where {c.countryName} businesses call us.
              </h2>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {c.problems.map((p, i) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
                  className="rounded-2xl border border-border bg-card p-6"
                  data-testid={`${c.slug}-problem-${i}`}
                >
                  <div className="font-heading font-bold text-lg leading-tight">{p.title}</div>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---- CORE SERVICES ---- */}
      {c.subServices?.length > 0 && (
        <section className="py-14 md:py-20" data-testid={`${c.slug}-services`}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-end justify-between gap-6 flex-wrap">
              <div className="max-w-2xl">
                <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">Services we deliver in {c.countryName}</div>
                <h2 className="mt-2 font-heading font-bold text-3xl md:text-4xl tracking-tight leading-[1.05]">
                  Core services for {c.market}.
                </h2>
              </div>
              <Link to="/services" className="text-sm text-primary hover:underline inline-flex items-center gap-1.5">
                All services <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {c.subServices.map((slug, i) => {
                const svc = SERVICE_CONFIGS[slug];
                if (!svc) return null;
                return (
                  <Link
                    key={slug}
                    to={`/services/${slug}`}
                    className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/50 transition-colors"
                    data-testid={`${c.slug}-service-${slug}`}
                  >
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
                      {String(i + 1).padStart(2, "0")} · Service
                    </div>
                    <div className="mt-2 font-heading font-bold text-xl leading-tight group-hover:text-primary transition-colors">
                      {svc.name}
                    </div>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                      {svc.hero?.subtitle?.split(". ")[0]}.
                    </p>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary">
                      Read more <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ---- RELATED CASE STUDIES (auto-filtered — hidden if none tagged) ---- */}
      {c.caseStudyTag && (
        <RelatedCaseStudies
          market={c.caseStudyTag}
          kicker="Case studies"
          title={`Verified ${c.countryName} projects`}
        />
      )}

      {/* ---- INDUSTRIES ---- */}
      <Industries />

      {/* ---- WHY CLOUDLAB ---- */}
      {c.whyPoints?.length > 0 && (
        <section className="py-14 md:py-20 bg-muted/30" data-testid={`${c.slug}-why`}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="max-w-2xl">
              <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">Why CloudLab</div>
              <h2 className="mt-2 font-heading font-bold text-3xl md:text-4xl tracking-tight leading-[1.05]">
                Why {c.countryName} businesses pick us.
              </h2>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {c.whyPoints.map((p, i) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
                  className="rounded-2xl border border-border bg-card p-6"
                  data-testid={`${c.slug}-why-${i}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4" strokeWidth={1.8} />
                  </div>
                  <div className="mt-4 font-heading font-bold text-base leading-tight">{p.title}</div>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---- FAQ (market-specific) ---- */}
      {c.faq?.length > 0 && (
        <FaqSection
          kicker={`${c.countryName} · FAQ`}
          title="Frequently asked questions"
          items={c.faq}
          testidRoot={`${c.slug}-faq`}
        />
      )}

      {/* ---- FOOTER CTA (dual) ---- */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading font-black tracking-tight text-4xl md:text-5xl leading-[1.05]">
            Have a software project?<br className="hidden md:block" /> Talk to our solutions team.
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            We&apos;re CloudLab&apos;s commercial team — not a career or job-application form.
            {" "}
            <Link to="/careers" className="underline hover:text-primary">Looking for a job? View careers here.</Link>
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => openEstimateWizard(`locale_${c.slug}_footer`)}
              className="group rounded-full pl-6 pr-2 py-2 inline-flex items-center gap-3 text-sm font-medium bg-primary text-primary-foreground shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.6)]"
              data-testid={`${c.slug}-footer-cta-primary`}
            >
              <span>Get a Free Project Estimate</span>
              <span className="w-8 h-8 rounded-full bg-background text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                <ArrowRight className="w-4 h-4" />
              </span>
            </button>
            <Link
              to="/contact"
              onClick={() => trackConsultation(`locale_${c.slug}_footer`)}
              className="rounded-full px-6 py-3 border border-border text-sm font-medium hover:bg-foreground/5 transition-colors inline-flex items-center gap-2"
              data-testid={`${c.slug}-footer-cta-secondary`}
            >
              <MessageCircle className="w-3.5 h-3.5" /> Book a 15-Minute Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
